import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Plus, Trash2, ChevronDown, ChevronUp,
  Mail, Phone, Link2, Building2, ExternalLink
} from 'lucide-react';
import { casePartyService } from '../../services/api';
import AddPartyModal from './AddPartyModal';
import ConfirmDialog from '../common/ConfirmDialog';

const ROLE_LABELS = {
  client: 'Client',
  opposing_party: 'Opposing Party',
  opposing_counsel: 'Opposing Counsel',
  co_counsel: 'Co-Counsel',
  witness: 'Witness',
  expert_witness: 'Expert Witness',
  judge: 'Judge',
  guardian_ad_litem: 'Guardian ad Litem',
  mediator: 'Mediator',
  insurer: 'Insurer',
  third_party: 'Third Party',
};

const ROLE_COLORS = {
  client: 'bg-emerald-100 text-emerald-800',
  opposing_party: 'bg-red-100 text-red-800',
  opposing_counsel: 'bg-red-50 text-red-700',
  co_counsel: 'bg-green-100 text-green-800',
  witness: 'bg-blue-100 text-blue-800',
  expert_witness: 'bg-indigo-100 text-indigo-800',
  judge: 'bg-purple-100 text-purple-800',
  guardian_ad_litem: 'bg-pink-100 text-pink-800',
  mediator: 'bg-yellow-100 text-yellow-800',
  insurer: 'bg-orange-100 text-orange-800',
  third_party: 'bg-gray-100 text-gray-800',
};

const CONTACT_TYPE_LABELS = {
  email: 'Email',
  phone_cell: 'Cell',
  phone_home: 'Home',
  phone_work: 'Work',
  fax: 'Fax',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  twitter: 'Twitter',
  instagram: 'Instagram',
  website: 'Website',
  other_social: 'Other',
};

const CasePartiesSection = ({ caseId }) => {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchParties = useCallback(async () => {
    try {
      const response = await casePartyService.getParties(caseId);
      const nonClientParties = (response.data || []).filter(p => p.role !== 'client');
      setParties(nonClientParties);
    } catch (err) {
      console.error('Failed to fetch case parties:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchParties();
  }, [fetchParties]);

  const toggleRow = (partyId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(partyId)) {
        next.delete(partyId);
      } else {
        next.add(partyId);
      }
      return next;
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await casePartyService.removeParty(caseId, deleteTarget);
      fetchParties();
    } catch (err) {
      console.error('Failed to remove party:', err);
    } finally {
      setDeleteTarget(null);
    }
  };

  const getPartyName = (party) => {
    if (party.client) {
      const parts = [party.client.first_name, party.client.last_name].filter(Boolean);
      return parts.length > 0 ? parts.join(' ') : party.client.company_name || 'Unknown';
    }
    if (party.person) {
      const parts = [party.person.first_name, party.person.last_name].filter(Boolean);
      return parts.length > 0 ? parts.join(' ') : party.person.company_name || 'Unknown';
    }
    return 'Unknown';
  };

  const getPartyCompany = (party) => {
    const entity = party.client || party.person;
    if (!entity) return null;
    if (entity.company_name && entity.first_name) return entity.company_name;
    return null;
  };

  const getContactSummary = (party) => {
    if (party.client) {
      const parts = [];
      if (party.client.email) parts.push(party.client.email);
      if (party.client.phone) parts.push(party.client.phone);
      return parts.join(' | ') || null;
    }
    if (!party.person?.contacts?.length) return null;
    const email = party.person.contacts.find(c => c.contact_type === 'email');
    const phone = party.person.contacts.find(c => ['phone_cell', 'phone_work', 'phone_home'].includes(c.contact_type));
    const parts = [];
    if (email) parts.push(email.value);
    if (phone) parts.push(phone.value);
    return parts.join(' | ') || null;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Parties & Persons</h3>
          {parties.length > 0 && (
            <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">
              {parties.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition-colors"
        >
          <Plus size={16} />
          Add Party
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600"></div>
        </div>
      ) : parties.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No parties added to this case yet.</p>
          <p className="text-xs text-gray-400 mt-1">
            Add opposing parties, witnesses, and other involved persons.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-medium text-gray-600">Name</th>
                <th className="text-left py-2 px-3 font-medium text-gray-600">Role</th>
                <th className="text-left py-2 px-3 font-medium text-gray-600 hidden md:table-cell">Contact</th>
                <th className="text-right py-2 px-3 font-medium text-gray-600 w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {parties.map(party => {
                const isExpanded = expandedRows.has(party.id);
                return (
                  <React.Fragment key={party.id}>
                    <tr
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleRow(party.id)}
                    >
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                          <div>
                            {(party.client || party.person) ? (
                              <Link
                                to={party.client ? `/clients/${party.client.id}` : `/persons/${party.person.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                              >
                                {getPartyName(party)}
                                <ExternalLink size={12} />
                              </Link>
                            ) : (
                              <div className="font-medium text-gray-900">{getPartyName(party)}</div>
                            )}
                            {getPartyCompany(party) && (
                              <div className="text-xs text-gray-500 flex items-center gap-1">
                                <Building2 size={12} />
                                {getPartyCompany(party)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ROLE_COLORS[party.role] || 'bg-gray-100 text-gray-800'}`}>
                          {ROLE_LABELS[party.role] || party.role}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-500 hidden md:table-cell">
                        {getContactSummary(party) || <span className="text-gray-300">-</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(party.id);
                          }}
                          className="text-gray-400 hover:text-red-500 p-1"
                          title="Remove from case"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={4} className="bg-gray-50 px-8 py-3">
                          <div className="space-y-2">
                            {/* Contacts — person with contacts array */}
                            {party.person?.contacts?.length > 0 && (
                              <div>
                                <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Contacts</h4>
                                <div className="space-y-1">
                                  {party.person.contacts.map(contact => (
                                    <div key={contact.id} className="flex items-center gap-2 text-sm">
                                      {contact.contact_type === 'email' ? (
                                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                                      ) : contact.contact_type.startsWith('phone') ? (
                                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                                      ) : (
                                        <Link2 className="h-3.5 w-3.5 text-gray-400" />
                                      )}
                                      <span className="text-gray-700">{contact.value}</span>
                                      <span className="text-gray-400 text-xs">
                                        ({CONTACT_TYPE_LABELS[contact.contact_type] || contact.contact_type})
                                        {contact.label && ` - ${contact.label}`}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Contacts — client with direct email/phone */}
                            {party.client && (party.client.email || party.client.phone) && (
                              <div>
                                <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Contacts</h4>
                                <div className="space-y-1">
                                  {party.client.email && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <Mail className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="text-gray-700">{party.client.email}</span>
                                    </div>
                                  )}
                                  {party.client.phone && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <Phone className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="text-gray-700">{party.client.phone}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Notes */}
                            {party.notes && (
                              <div>
                                <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Notes</h4>
                                <p className="text-sm text-gray-700">{party.notes}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Party Modal */}
      <AddPartyModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        caseId={caseId}
        onSuccess={fetchParties}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Remove Party"
        message="Remove this party from the case? The person record will be preserved."
        confirmText="Remove"
        variant="danger"
      />
    </div>
  );
};

export default CasePartiesSection;
